import React from "react";
import Table, { type Column } from "Components/Table/Table";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import Button from "Components/Common/Button/Button";
import { CO_STATUS } from "Pages/GcPrecon/ChangeOrder/constants/changeOrderStatus";

// ── Types ──────────────────────────────────────────────────────────────────────

interface COAmount {
  amount?: number;
  currency?: { symbol?: string; code?: string };
}

interface CODocument {
  id?: string;
  title?: string;
  name?: string;
  fileName?: string;
  key?: string;
  document?: { fileName?: string; url?: string };
}

interface COLienItem {
  id?: string;
  description?: string;
  displayOrder?: number;
  amount?: COAmount;
  aiaItem?: { id?: string; itemCode?: number; itemName?: string };
  subContractor?: { id?: string; companyName?: string } | null;
}

interface COActivity {
  title: string;
  description?: string;
  meta?: { companyName?: string; date?: string; days?: number };
}

export interface CODetailData {
  id: string;
  number?: string;
  coNumber?: string;
  title?: string;
  description?: string;
  date?: string;
  cause?: string;
  tradeDivisions?: string;
  status?: string;
  amount?: COAmount;
  changeAmount?: COAmount;
  totalAmount?: COAmount;
  revisedContractValueWithPendingCO?: COAmount;
  extendedDays?: number;
  scheduleImpact?: number | string;
  scheduleImpactDays?: number;
  projectName?: string;
  publishedByCompany?: string;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
  approvedAt?: string;
  lenderApprovedAt?: string;
  lenderRejectedAt?: string;
  gc?: { companyName?: string };
  contractor?: { companyName?: string };
  project?: { name?: string };
  lender?: { bankName?: string; companyName?: string };
  documents?: CODocument[];
  lienItems?: COLienItem[];
  activities?: COActivity[];
  notes?: string;
}

// ── Status constants (must match backend values) ───────────────────────────────

const S_APPROVED = CO_STATUS.APPROVED;
const S_REJECTED = CO_STATUS.REJECTED;
const S_PENDING = CO_STATUS.PENDING;

// ── Helpers ────────────────────────────────────────────────────────────────────

function getSymbol(raw?: COAmount): string {
  return raw?.currency?.symbol ?? "$";
}

function formatMoney(raw?: COAmount): string {
  if (!raw || raw.amount == null) return "—";
  return `${getSymbol(raw)}${Math.abs(raw.amount).toLocaleString("en-US")}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatScheduleImpact(val?: number | string): string {
  if (val == null || val === "" || val === 0) return "None";
  if (typeof val === "string") return val;
  return `${val > 0 ? "+" : ""}${val} day${Math.abs(val) !== 1 ? "s" : ""}`;
}

function getAmount(co: CODetailData): COAmount | undefined {
  return co.totalAmount ?? co.amount ?? co.changeAmount;
}

function getDisplayAmount(co: CODetailData): COAmount | undefined {
  return co.revisedContractValueWithPendingCO ?? getAmount(co);
}

function buildTimeline(co: CODetailData) {
  if (co.activities && co.activities.length > 0) {
    return co.activities.map((act, i) => ({
      id: i + 1,
      status:
        act.title.toLowerCase().includes("awaiting bank approval") ||
        act.title.toLowerCase().includes("awaiting owner approval")
          ? "pending"
          : "done",
      title: act.title,
      meta:
        [act.meta?.companyName, formatDate(act.meta?.date)]
          .filter(Boolean)
          .join(" — ") ||
        act.description ||
        "",
    }));
  }
  const isPending = co.status === S_PENDING;
  const gcName =
    co.publishedByCompany ??
    co.gc?.companyName ??
    co.contractor?.companyName ??
    "GC";
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return [
    {
      id: 1,
      status: "done",
      title: "Submitted by GC",
      meta: `${gcName} — ${formatDate(submittedDate)}`,
    },
    {
      id: 2,
      status: "done",
      title: "CSC New England notified",
      meta: "CSC notified upon submission",
    },
    {
      id: 3,
      status: isPending ? "pending" : "done",
      title: isPending ? "Awaiting Owner Approval" : "Decision recorded",
      meta: isPending
        ? "Pending your review"
        : (co.lenderApprovedAt ?? co.approvedAt)
          ? `Resolved ${formatDate(co.lenderApprovedAt ?? co.approvedAt)}`
          : "Resolved",
    },
  ];
}

// ── Component ──────────────────────────────────────────────────────────────────

interface ChangeOrderDetailsViewProps {
  co: CODetailData;
  loading: boolean;
  onClose: () => void;
}

const ChangeOrderDetailsView = ({
  co,
  loading,
  onClose,
}: ChangeOrderDetailsViewProps) => {
  const amt = getDisplayAmount(co);
  const amountDisplay = amt ? `+${formatMoney(amt)}` : "—";
  const submittedOn = co.publishedAt ?? co.submittedAt;
  const daysElapsed = submittedOn
    ? Math.floor((Date.now() - new Date(submittedOn).getTime()) / 86_400_000)
    : null;
  const timeline = buildTimeline(co);
  return (
    <>
      <article className="coDetailCard">
        {/* ── Header ── */}
        <div className="coDetailHeader">
          <div>
            <p className="coLabel">
              {co.status === S_APPROVED
                ? "Change Order - Approved"
                : co.status === S_REJECTED
                  ? "Change Order - Rejected"
                  : "Change Order"}
            </p>
            <h2>
              {co.number ?? co.coNumber ?? "—"} — {co.title ?? "—"}
            </h2>
            <p className="coMeta">
              {[
                co.projectName ?? co.project?.name,
                (co.publishedByCompany ??
                co.gc?.companyName ??
                co.contractor?.companyName)
                  ? `Submitted by ${co.publishedByCompany ?? co.gc?.companyName ?? co.contractor?.companyName}`
                  : null,
                formatDate(co.publishedAt ?? co.submittedAt),
              ]
                .filter(Boolean)
                .join(" | ")}
            </p>
          </div>
          <div className="coAmountBox">
            <span>Change Amount</span>
            <strong>{amountDisplay}</strong>
            {co.status === S_APPROVED ? (
              <span className="badge bApproved">
                <span className="dot" />
                Owner Approved
              </span>
            ) : co.status === S_REJECTED ? (
              <span className="badge bRejected">
                <span className="dot" />
                Rejected
              </span>
            ) : null}
          </div>
        </div>

        {/* ── Impact bar ── */}
        <div className="impactBar">
          <div>
            <strong className="up">{amountDisplay}</strong>
            <span>Contract Increase</span>
          </div>
          <div>
            <strong className="amber">
              {formatScheduleImpact(
                co.extendedDays ?? co.scheduleImpact ?? co.scheduleImpactDays,
              )}
            </strong>
            <span>Schedule Impact</span>
          </div>
          <div>
            <strong>{co.documents?.length ?? 0}</strong>
            <span>Documents Attached</span>
          </div>
          <div>
            <strong>
              {daysElapsed != null
                ? `${daysElapsed} day${daysElapsed !== 1 ? "s" : ""}`
                : "—"}
            </strong>
            <span>Days Since Submitted</span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="coBody">
          {loading ? (
            <TableSkeleton cols={1} rows={4} />
          ) : (
            <>
              <h3>Description of Change</h3>
              <p>{co.description ?? "No description provided."}</p>

              {(co.cause || co.tradeDivisions) && (
                <div className="coMetaGrid">
                  {co.cause && (
                    <div>
                      <h3>Reason / Cause</h3>
                      <p>{co.cause}</p>
                    </div>
                  )}
                  {co.tradeDivisions && (
                    <div>
                      <h3>Trade Division</h3>
                      <p>{co.tradeDivisions}</p>
                    </div>
                  )}
                </div>
              )}

              {co.lienItems &&
                co.lienItems.length > 0 &&
                (() => {
                  const totalAmt = co.lienItems!.reduce(
                    (sum, item) => sum + (item.amount?.amount ?? 0),
                    0,
                  );
                  const columns: Column<COLienItem>[] = [
                    {
                      header: (
                        <>
                          A<span className="tableSubTitle">Item #</span>
                        </>
                      ),
                      className: "center",
                      render: (item) => item.displayOrder ?? "—",
                    },
                    {
                      header: "CSI Code",
                      render: (item) => item.aiaItem?.itemCode ?? "—",
                    },
                    {
                      header: (
                        <>
                          B
                          <span className="tableSubTitle">
                            Description of Work
                          </span>
                        </>
                      ),
                      render: (item) => item.description ?? "—",
                    },
                    {
                      header: (
                        <>
                          C
                          <span className="tableSubTitle">Scheduled Value</span>
                        </>
                      ),
                      className: "num",
                      render: (item) => {
                        const a = item.amount?.amount ?? 0;
                        const sym = item.amount?.currency?.symbol ?? "$";
                        return `${sym}${a.toLocaleString("en-US")}`;
                      },
                    },
                    {
                      header: "% of CONTRACT",
                      className: "num",
                      render: (item) => {
                        const a = item.amount?.amount ?? 0;
                        return totalAmt > 0
                          ? `${((a / totalAmt) * 100).toFixed(1)}%`
                          : "0.0%";
                      },
                    },
                    {
                      header: "Subcontractor / Vendor",
                      render: (item) => item.subContractor?.companyName ?? "—",
                    },
                  ];
                  const footer: React.ReactNode[] = [
                    <span key="label" className="coTableLabel">
                      Total Change Order Amount
                    </span>,
                    null,
                    null,
                    <span key="total" className="coTableAmount">
                      {formatMoney(getDisplayAmount(co))}
                    </span>,
                    <span key="pct" className="muted">
                      {totalAmt > 0 ? "100.0%" : "0.0%"}
                    </span>,
                    null,
                  ];
                  return (
                    <>
                      <h3>Budget Breakdown</h3>
                      <Table<COLienItem>
                        data={co.lienItems!}
                        columns={columns}
                        footerRow={footer}
                        tableClassName="coTable"
                        footerRowClassName="coTableFooter"
                      />
                    </>
                  );
                })()}

              <h3>Attached Documents</h3>
              {co.documents && co.documents.length > 0 ? (
                <div className="fileChipWrap">
                  {co.documents.map((doc, i) => {
                    const label =
                      doc.title ??
                      doc.document?.fileName ??
                      doc.fileName ??
                      doc.name ??
                      doc.key ??
                      `Document ${i + 1}`;
                    const url = doc.document?.url;
                    return url ? (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fileChip"
                      >
                        {label}
                      </a>
                    ) : (
                      <span key={i} className="fileChip">
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p>No documents attached.</p>
              )}

              <h3>Activity</h3>
              <ul className="timeline">
                {timeline.map((item) => (
                  <li key={item.id}>
                    <span className={`tlDot ${item.status}`.trim()}>
                      {item.status === "pending" ? "..." : "✓"}
                    </span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.meta}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {(co.status === S_APPROVED || co.status === S_REJECTED) && (
                <div
                  className={`coDecisionBanner ${
                    co.status === S_APPROVED
                      ? "decisionApproved"
                      : "decisionRejected"
                  }`}
                >
                  <span className="decisionIcon">
                    {co.status === S_APPROVED ? "✓" : "✕"}
                  </span>
                  <div className="decisionBody">
                    <strong>
                      {co.status === S_APPROVED
                        ? "Approved by Owner"
                        : "Rejected by Owner"}
                    </strong>
                    <p>
                      {co.status === S_APPROVED
                        ? "This change order has been approved. The project contract value has been updated."
                        : "This change order has been rejected. The GC has been notified and may revise and resubmit."}
                    </p>
                    {(co.lenderApprovedAt ?? co.lenderRejectedAt) && (
                      <span className="decisionDate">
                        {formatDate(
                          (co.status === S_APPROVED
                            ? co.lenderApprovedAt
                            : co.lenderRejectedAt) ?? "",
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </article>
      {/* ── Back action ── */}
      <div className="coDetailViewActions">
        <Button type="button" className="btn btnSecondary" onClick={onClose}>
          ← Back to Change Orders
        </Button>
      </div>
    </>
  );
};

export default ChangeOrderDetailsView;
